import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Minimal ambient shim for SpeechRecognition since TS lib-dom doesn't
// ship types for it yet (browser-prefixed, non-standard).
type SR = {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  onresult: ((e: SpeechRecognitionResultEvent) => void) | null
  onend: (() => void) | null
  onerror: ((e: { error?: string }) => void) | null
  onstart: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}
interface SpeechRecognitionResultEvent {
  resultIndex: number
  results: ArrayLike<{
    isFinal: boolean
    0: { transcript: string }
  }>
}
type SRCtor = { new (): SR }

function getRecognitionCtor(): SRCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SRCtor
    webkitSpeechRecognition?: SRCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export interface UseSpeechRecognitionResult {
  supported: boolean
  listening: boolean
  /**
   * True for a short window after each speech-recognition result event fires -
   * a proxy for "the user is actively speaking right now". The UI uses this
   * to flash the mic waveform only during voiced sections so the icon doesn't
   * stay red during silence.
   */
  speaking: boolean
  transcript: string         // finalised so far this session
  interim: string            // in-progress (last chunk not yet finalised)
  error: string | null
  start: () => void
  stop: () => void
  reset: () => void
}

export function useSpeechRecognition(
  opts: { lang?: string; onFinal?: (text: string) => void } = {},
): UseSpeechRecognitionResult {
  const { lang = 'en-US', onFinal } = opts
  const Ctor = useMemo(getRecognitionCtor, [])
  const recognitionRef = useRef<SR | null>(null)
  const speakingTimerRef = useRef<number | null>(null)
  const [listening, setListening] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)

  const supported = !!Ctor

  useEffect(() => {
    return () => {
      // Stop cleanly on unmount.
      try { recognitionRef.current?.abort() } catch { /* ignore */ }
      recognitionRef.current = null
      if (speakingTimerRef.current) {
        window.clearTimeout(speakingTimerRef.current)
        speakingTimerRef.current = null
      }
    }
  }, [])

  const start = useCallback(() => {
    if (!Ctor) {
      setError('Voice input is not supported in this browser.')
      return
    }
    try {
      const r = new Ctor()
      r.continuous = true
      r.interimResults = true
      r.lang = lang
      r.maxAlternatives = 1
      r.onstart = () => {
        setListening(true)
        setError(null)
      }
      r.onerror = (e) => {
        const code = e?.error || 'error'
        if (code === 'aborted') return
        setError(code === 'not-allowed' ? 'Microphone permission denied.' : `Speech error: ${code}`)
      }
      r.onend = () => {
        setListening(false)
        setSpeaking(false)
        if (speakingTimerRef.current) {
          window.clearTimeout(speakingTimerRef.current)
          speakingTimerRef.current = null
        }
      }
      r.onresult = (e) => {
        let finalChunk = ''
        let interimChunk = ''
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i]
          const text = res[0].transcript
          if (res.isFinal) finalChunk += text
          else interimChunk += text
        }
        if (finalChunk) {
          setTranscript((prev) => {
            const combined = (prev + finalChunk).replace(/\s+/g, ' ').trim()
            if (onFinal) onFinal(combined)
            return combined
          })
        }
        setInterim(interimChunk.trim())
        // Pulse the speaking flag on every result event, with a short tail
        // timer so the waveform keeps animating through brief silences but
        // stops when the user actually pauses.
        if (finalChunk || interimChunk) {
          setSpeaking(true)
          if (speakingTimerRef.current) window.clearTimeout(speakingTimerRef.current)
          speakingTimerRef.current = window.setTimeout(() => {
            setSpeaking(false)
            speakingTimerRef.current = null
          }, 700)
        }
      }
      recognitionRef.current = r
      r.start()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start voice input.')
    }
  }, [Ctor, lang, onFinal])

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop() } catch { /* ignore */ }
  }, [])

  const reset = useCallback(() => {
    setTranscript('')
    setInterim('')
    setError(null)
    setSpeaking(false)
    if (speakingTimerRef.current) {
      window.clearTimeout(speakingTimerRef.current)
      speakingTimerRef.current = null
    }
  }, [])

  return { supported, listening, speaking, transcript, interim, error, start, stop, reset }
}
